import store from '@/store';

/**
 * Creates and injects the Salesforce initialization script
 */
function injectInitializationScript() {
  const configScript = document.createElement('script');
  configScript.textContent = `
    window.initEmbeddedMessaging = function initEmbeddedMessaging() {
      try {
        if (!window.embeddedservice_bootstrap) {
          return;
        }

        embeddedservice_bootstrap.settings.language = 'en_US';

        embeddedservice_bootstrap.init(
          '${import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_ORG_ID}',
          '${import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_EMBEDDED_SERVICE_NAME}',
          '${import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_ESW_URL}',
          { scrt2URL: '${import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_SCRT2_URL}' }
        );

        var prechatData = window.acvEnhancedChatPrechatData || {};
        var sFirstName = prechatData.firstName || '';
        var sLastName = prechatData.lastName || '';
        var sEmail = prechatData.email || '';
        var sUserID = prechatData.userID || '';
        var sPlatform = prechatData.platform || 'Web';

        function applyPrechatViaApi() {
          try {
            if (!window.embeddedservice_bootstrap || !window.embeddedservice_bootstrap.prechatAPI) return;
            if (!window.embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields) return;

            window.embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
              _firstName: {
                value: sFirstName,
                isEditableByEndUser: true,
              },
              _lastName: {
                value: sLastName,
                isEditableByEndUser: true,
              },
              _email: {
                value: sEmail,
                isEditableByEndUser: true,
              },
            });

            if (window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields) {
              window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
                userID: sUserID,
                platform: sPlatform,
              });
            }
          } catch (e) {
            // Ignore API failures
          }
        }

        window.addEventListener('onEmbeddedMessagingReady', function() {
          applyPrechatViaApi();
        });

        window.addEventListener('acv-prechat-refresh', function() {
          prechatData = window.acvEnhancedChatPrechatData || {};
          sFirstName = prechatData.firstName || '';
          sLastName = prechatData.lastName || '';
          sEmail = prechatData.email || '';
          sUserID = prechatData.userID || '';
          sPlatform = prechatData.platform || 'Web';
          applyPrechatViaApi();
        });
      } catch (err) {
        // Ignore initialization failures
      }
    }
  `;
  document.body.appendChild(configScript);
}

function getPrechatData() {
  const currentUser = store.getters.currentUser || {};
  const fullName = currentUser.full_name || currentUser.fullName || '';
  const [firstName = '', lastName = ''] = fullName.split(' ');

  const proxyMeta = window.acvEnhancedChatProxyMetadata || {};
  const proxyFullName = proxyMeta.fullName || proxyMeta.full_name || '';
  const [proxyFirst = '', proxyLast = ''] = proxyFullName.split(' ');
  const resolvedUserId = currentUser.user_id
    || proxyMeta.userID
    || proxyMeta.userId
    || proxyMeta.user_id
    || '';
  const userID = resolvedUserId === '' ? '' : String(resolvedUserId);

  const prechatData = {
    firstName: currentUser.first_name
      || currentUser.firstName
      || firstName
      || proxyMeta.firstName
      || proxyMeta.first_name
      || proxyFirst,
    lastName: currentUser.last_name
      || currentUser.lastName
      || lastName
      || proxyMeta.lastName
      || proxyMeta.last_name
      || proxyLast,
    email: currentUser.email || proxyMeta.email || '',
    userID,
    platform: 'Web',
    FirstName: currentUser.first_name
      || currentUser.firstName
      || firstName
      || proxyMeta.firstName
      || proxyMeta.first_name
      || proxyFirst,
    LastName: currentUser.last_name
      || currentUser.lastName
      || lastName
      || proxyMeta.lastName
      || proxyMeta.last_name
      || proxyLast,
    Email: currentUser.email || proxyMeta.email || '',
    UserID: userID,
    Platform: 'Web',
  };

  return prechatData;
}

function getEnhancedChatOrigin() {
  try {
    return new URL(import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_ESW_URL).origin;
  } catch (e) {
    return '';
  }
}

function getEnhancedChatScrtOrigin() {
  try {
    return new URL(import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_SCRT2_URL).origin;
  } catch (e) {
    return '';
  }
}

/**
 * Loads the Salesforce bootstrap script
 */
function loadBootstrapScript() {
  const bootstrapScript = document.createElement('script');
  bootstrapScript.src = import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_SCRIPT_URL;

  bootstrapScript.onload = () => {
    if (window.initEmbeddedMessaging) {
      window.initEmbeddedMessaging();
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(() => {
      attempts += 1;
      if (window.initEmbeddedMessaging) {
        clearInterval(interval);
        window.initEmbeddedMessaging();
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 300);
  };

  document.body.appendChild(bootstrapScript);
}

const SQUARE_CHAT_ICON_VIEWBOX = '0 0 24 24';
const SQUARE_CHAT_ICON_OUTLINE_PATH = 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z';

// Salesforce re-renders this icon's markup (viewBox included) whenever the launcher toggles
// open/closed, wiping any prior override. Re-check on every observer tick and every matching
// node (not just the first) so it self-heals instead of only applying once.
// .minimizedButtonIconChat is a *different* icon Salesforce swaps in for the "minimized while a
// conversation is active" launcher (embeddedmessaging-minimized-state), distinct from the
// idle/closed launcher's .embeddedMessagingIconChat.
function replaceChatIconWithSquareBubble() {
  const icons = document.querySelectorAll('.embeddedMessagingIconChat, .minimizedButtonIconChat');

  for (let i = 0; i < icons.length; i += 1) {
    const icon = icons[i];
    if (icon.getAttribute('viewBox') !== SQUARE_CHAT_ICON_VIEWBOX) {
      icon.setAttribute('viewBox', SQUARE_CHAT_ICON_VIEWBOX);
      icon.innerHTML = `
        <path d="${SQUARE_CHAT_ICON_OUTLINE_PATH}" fill="none" stroke="#FFFFFF" stroke-width="2.5" stroke-linejoin="round"></path>
        <rect x="6" y="5.5" width="12" height="2" fill="#FFFFFF"></rect>
        <rect x="6" y="9" width="12" height="2" fill="#FFFFFF"></rect>
        <rect x="6" y="12.5" width="9" height="2" fill="#FFFFFF"></rect>
      `;
    }
  }
}

// The chat launcher stays put; any other fixed, bottom-right-anchored element that opts in via
// this attribute (e.g. Title Hub's shipment queue button) shifts left to stay clear of it,
// instead of this file needing to know that element exists. Measuring the chat widget's actual
// rendered position (rather than assuming a fixed launcher size) means this keeps working
// whether the launcher is idle, minimized, or the full conversation panel is open (which is far
// wider than the launcher FAB).
const CHAT_CLEARANCE_SELECTOR = '[data-avoid-chat-launcher]';

function getVisibleElementRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  if (window.getComputedStyle(el).display === 'none') return null;
  return rect;
}

function getChatWidgetClearance() {
  const GAP = 16;
  // Prefer the open conversation panel (widest) over the minimized FAB, and that over the idle
  // launcher FAB (narrowest), since only one of these is ever actually visible at a time.
  const rect = getVisibleElementRect('.embeddedMessagingFrame')
    || getVisibleElementRect('.minimizedButton')
    || getVisibleElementRect('.embeddedMessagingConversationButton');
  if (!rect) return 0;
  return Math.max(0, Math.round(window.innerWidth - rect.left + GAP));
}

function applyChatClearanceToTargets() {
  const clearance = `${getChatWidgetClearance()}px`;
  document.querySelectorAll(CHAT_CLEARANCE_SELECTOR).forEach((el) => {
    el.style.setProperty('--chat-clearance', clearance);
  });
}

function applyLauncherOffsetWithObserver() {
  if (window.acvLauncherOffsetObserver) return;

  const mobileQuery = window.matchMedia('(max-width: 767px)');

  const mobileStyles = [
    ['.embeddedMessagingConversationButtonWrapper', {
      top: 'auto',
      left: 'auto',
      right: '16px',
      '--eswButtonRight': '16px',
    }],
    ['.embeddedMessagingConversationButton', {
      right: '16px',
    }],
    ['#embedded-messaging', {
      position: 'fixed',
      'z-index': '999999',
      overflow: 'hidden',
    }],
  ];

  // Per Figma: solid ACV Primary/base fill (no gradient), fixed 56x56 circle, elevation shadow.
  // Applied to both the idle launcher and the "minimized while a conversation is active"
  // launcher (.minimizedButton), since Salesforce renders them as separate components.
  const brandStyles = [
    ['.embeddedMessagingConversationButton', {
      '--eswButtonColor': '#F26522',
      '--eswButtonHoverColor': '#F26522',
      background: '#F26522',
      width: '56px',
      height: '56px',
      'border-radius': '28px',
      'box-shadow': '0px 1px 10px rgba(0,0,0,0.12), 0px 2px 4px rgba(0,0,0,0.2)',
    }],
    ['.embeddedMessagingIconChat', {
      width: '24px',
      height: '24px',
    }],
    ['.minimizedButton', {
      background: '#F26522',
      width: '56px',
      height: '56px',
      'border-radius': '28px',
      'box-shadow': '0px 1px 10px rgba(0,0,0,0.12), 0px 2px 4px rgba(0,0,0,0.2)',
    }],
    ['.minimizedButtonIconChat', {
      width: '24px',
      height: '24px',
    }],
  ];

  const applyStyles = () => {
    replaceChatIconWithSquareBubble();

    // brand styles always apply, regardless of viewport. Applied to every match (not just the
    // first) since Salesforce can render more than one node sharing these classes.
    brandStyles.forEach(([selector, styles]) => {
      document.querySelectorAll(selector).forEach((el) => {
        Object.keys(styles).forEach((prop) => {
          if (el.style.getPropertyValue(prop) !== styles[prop]) {
            el.style.setProperty(prop, styles[prop], 'important');
          }
        });
      });
    });

    // go through each mobile array selector and apply values to keys as important
    mobileStyles.forEach(([selector, styles]) => {
      document.querySelectorAll(selector).forEach((el) => {
        Object.keys(styles).forEach((prop) => {
          if (mobileQuery.matches) {
            // towards mobile
            if (el.style.getPropertyValue(prop) !== styles[prop]) {
              el.style.setProperty(prop, styles[prop], 'important');
            }
          } else if (el.style.getPropertyValue(prop) !== '') {
            // towards desktop
            el.style.removeProperty(prop);
          }
        });
      });
    });

    applyChatClearanceToTargets();
  };

  const observerConfig = {
    childList: true,
    subtree: true,
    attributes: true,
    // viewBox is included because Salesforce resets the launcher icon's viewBox (independent
    // of its style/class) when the conversation panel opens/closes
    attributeFilter: ['style', 'class', 'viewBox'],
  };

  let widgetObserver;

  // applyStyles() itself mutates style/class/viewBox on nodes the observer below watches, so a
  // naive "observe forever" setup re-triggers its own callback on every write, in a runaway
  // feedback loop (and since it's scoped to document.body, *any* unrelated mutation elsewhere in
  // the app also fires it). Disconnecting before mutating and re-observing after closes that
  // loop for good. Re-targeting onto #embedded-messaging (once Salesforce has mounted it),
  // instead of staying on document.body indefinitely, also keeps it from reacting to unrelated
  // app-wide DOM changes once the widget exists.
  const reapplyWithoutLooping = () => {
    if (widgetObserver) widgetObserver.disconnect();
    applyStyles();

    if (!widgetObserver) widgetObserver = new MutationObserver(reapplyWithoutLooping);
    const widgetRoot = document.getElementById('embedded-messaging') || document.body;
    widgetObserver.observe(widgetRoot, observerConfig);
  };

  reapplyWithoutLooping();
  mobileQuery.addEventListener('change', reapplyWithoutLooping);

  // Clearance targets (e.g. Title Hub's shipment queue button) live in a completely different
  // part of the DOM from the chat widget and mount/unmount independently of anything Salesforce
  // does, so they won't be caught by widgetObserver above (which is scoped to the widget itself
  // once mounted). Track how many currently exist and re-apply on any change, so a newly-mounted
  // target immediately picks up its correct offset relative to whatever chat state already
  // exists, without this file needing to know what that target is.
  let chatClearanceTargetCount = document.querySelectorAll(CHAT_CLEARANCE_SELECTOR).length;
  const chatClearanceTargetObserver = new MutationObserver(() => {
    const count = document.querySelectorAll(CHAT_CLEARANCE_SELECTOR).length;
    if (count !== chatClearanceTargetCount) {
      chatClearanceTargetCount = count;
      reapplyWithoutLooping();
    }
  });
  chatClearanceTargetObserver.observe(document.body, { childList: true, subtree: true });

  // Opening/minimizing/closing the widget (launcher button, header minimize/close controls,
  // etc.) makes Salesforce re-render the button and icon, which can momentarily race the
  // MutationObserver above. Re-apply shortly after any click inside the widget.
  document.body.addEventListener('click', (event) => {
    const matched = event.target.closest?.('.embeddedMessagingConversationButton, #embedded-messaging');
    if (matched) {
      setTimeout(reapplyWithoutLooping, 50);
      setTimeout(reapplyWithoutLooping, 300);
      setTimeout(reapplyWithoutLooping, 800);
    }
  });

  window.acvLauncherOffsetObserver = widgetObserver;
}

/**
 * Checks if environment variable is enabled OR if in dev mode
 * @returns {boolean}
 */
function isEnabledInEnvironment() {
  return import.meta.env.VITE_SALESFORCE_ENHANCED_CHAT_ENABLED === 'true';
}

/**
 * Checks if scripts are already loaded
 * @returns {boolean}
 */
function isAlreadyLoaded() {
  return !!window.embeddedservice_bootstrap;
}

/**
 * Checks if feature flag is enabled
 * @returns {boolean}
 */
function isFeatureFlagEnabled() {
  return true;
}

/**
 * Checks if the current route is a MYACV page
 * @param {Object} route - The current route object
 * @returns {boolean}
 */
function isMyACVPage(route) {
  if (!route || !route.path) return false;
  const path = route.path.toLowerCase();
  return path.includes('/myacv') || path.startsWith('/titles');
}

/**
 * Checks if the viewport is at or below the mobile breakpoint used elsewhere in this file
 * (e.g. the launcher repositioning in applyLauncherOffsetWithObserver)
 * @returns {boolean}
 */
function isMobileViewport() {
  return window.matchMedia('(max-width: 767px)').matches;
}

// AcvBottomSheet (@acv/shared-vuejs) is a shared module across the host and every MFE remote
// (see vite.config's federation "shared" list), so a remote's own bottom-sheet modals (e.g. the
// Seller Control Room "Dealerships" filter) render this exact class, letting the host detect an
// open sheet generically without needing anything from the remote itself.
function isBottomSheetExpanded() {
  return !!document.querySelector('.acv-bottom-sheet__drawer--expanded');
}

/**
 * Checks if the route meta indicates chat should be hidden
 * @param {Object} route - The current route object
 * @returns {boolean}
 */
function shouldHideChatOnRoute(route) {
  if (route?.meta?.hideMyACVChat === true) return true;
  if (route?.meta?.hideMyACVChatOnMobile === true && isMobileViewport()) return true;
  if (route?.meta?.hideMyACVChatOnMobileWhileModalOpen === true
    && isMobileViewport() && isBottomSheetExpanded()) return true;
  return false;
}

// Tracks the most recent route passed to useSalesforceEnhancedChat, and whether a bottom sheet
// was expanded as of the last check, so the observer below can re-run the same hide/show
// decision when a sheet opens/closes without a route change.
let lastKnownRoute = null;
let wasBottomSheetExpanded = false;

/**
 * Determines if Enhanced Chat should be loaded based on all conditions
 * @param {Object} route - The current route object
 * @returns {boolean}
 */
function shouldLoadEnhancedChat(route) {
  if (!isEnabledInEnvironment()) return false;
  if (isAlreadyLoaded()) return false;
  if (!isMyACVPage(route)) return false;
  if (!isFeatureFlagEnabled()) return false;
  if (shouldHideChatOnRoute(route)) return false;

  return true;
}

/**
 * Loads all Salesforce Enhanced Chat scripts
 */
function loadSalesforceEnhancedChat() {
  window.acvEnhancedChatPrechatData = getPrechatData();
  window.acvEnhancedChatOrigin = getEnhancedChatOrigin();
  window.acvEnhancedChatScrtOrigin = getEnhancedChatScrtOrigin();

  injectInitializationScript();
  loadBootstrapScript();
  applyLauncherOffsetWithObserver();
}

// .minimizedButton is Salesforce's restore button for an existing/minimized conversation — a
// separate component from the idle launcher (.embeddedMessagingConversationButton) and not
// covered by the "embeddedMessaging" substring match, so it must be listed explicitly or a
// tester with an already-open/minimized session keeps seeing it on routes that opt out of chat.
const CHAT_VISIBILITY_SELECTOR = '.embeddedMessagingFrame, .embeddedMessagingConversationButton, .minimizedButton, [class*="embeddedMessaging"]';

/**
 * Hides the Salesforce chat widget
 */
function hideSalesforceChat() {
  // Wait a bit to ensure elements are in DOM
  setTimeout(() => {
    const chatElements = document.querySelectorAll(CHAT_VISIBILITY_SELECTOR);
    chatElements.forEach((el) => {
      const element = el;
      element.style.display = 'none';
    });
  }, 100);
}

/**
 * Un-hiding the launcher button only makes the FAB visible — it does not expand
 * the conversation panel. Salesforce only opens the panel in response to a real
 * click on the launcher button, so simulate one when there's truly no existing session.
 * A minimized conversation renders its own restore button (.minimizedButton) instead
 * of the idle launcher, so that takes priority when present.
 *
 * IMPORTANT: never fall through to clicking the idle launcher
 * (.embeddedMessagingConversationButton) once a conversation session already exists (open or
 * minimized) — that button starts a brand new session rather than restoring the existing one,
 * which spawns a second widget stacked behind the first instead of un-minimizing it.
 */
function openSalesforceChatConversation() {
  const minimizedButton = document.querySelector('.minimizedButton');
  if (minimizedButton) {
    minimizedButton.click();
    return;
  }

  // A session frame existing at all (even hidden/collapsed while minimized) means a
  // conversation is already active — bail rather than risk starting a duplicate one.
  if (document.querySelector('.embeddedMessagingFrame')) return;

  const launcherButton = document.querySelector('.embeddedMessagingConversationButton');
  if (!launcherButton) return;

  launcherButton.click();
}

/**
 * Shows the Salesforce chat widget
 */
function showSalesforceChat(options = {}) {
  const { openConversation = false } = options;
  applyLauncherOffsetWithObserver();
  // Wait a bit to ensure elements are in DOM
  setTimeout(() => {
    const chatElements = document.querySelectorAll(CHAT_VISIBILITY_SELECTOR);
    chatElements.forEach((el) => {
      const element = el;
      element.style.display = '';
    });

    if (openConversation) {
      openSalesforceChatConversation();
    }
  }, 100);
}

export function reevaluateBottomSheetChatVisibility() {
  const isExpanded = isBottomSheetExpanded();
  if (isExpanded === wasBottomSheetExpanded) return;
  wasBottomSheetExpanded = isExpanded;

  if (!lastKnownRoute || !isAlreadyLoaded()) return;
  if (shouldHideChatOnRoute(lastKnownRoute)) {
    hideSalesforceChat();
  } else if (isMyACVPage(lastKnownRoute)) {
    showSalesforceChat();
  }
}

/**
 * Watches for AcvBottomSheet expand/collapse anywhere on the page (host or MFE remote) and
 * re-applies the current route's chat visibility decision when that state changes — needed
 * because opening/closing a modal doesn't trigger a route change, so nothing else would
 * re-evaluate hideMyACVChatOnMobileWhileModalOpen.
 */
function observeBottomSheetForChatVisibility() {
  if (window.acvBottomSheetChatObserver) return;

  wasBottomSheetExpanded = isBottomSheetExpanded();
  let pending = false;

  const observer = new MutationObserver(() => {
    // Coalesce bursts of mutations (e.g. the sheet's open/close transition) into one check
    if (pending) return;
    pending = true;
    setTimeout(() => {
      pending = false;
      reevaluateBottomSheetChatVisibility();
    }, 0);
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    subtree: true,
  });

  window.acvBottomSheetChatObserver = observer;
}

/**
 * Main composable function to initialize Salesforce Enhanced Chat
 * Called from App.vue mounted hook, so DOM is already ready
 * @param {Object} route - The current route object from vue-router
 */
export default function useSalesforceEnhancedChat(route) {
  lastKnownRoute = route;
  // Only routes that actually opt into this behavior need the observer — avoids creating one
  // (and the DOM/perf cost of watching the whole document) on every other MYACV page.
  if (route?.meta?.hideMyACVChatOnMobileWhileModalOpen === true) {
    observeBottomSheetForChatVisibility();
  }

  const isMyACV = isMyACVPage(route);
  const isFlagEnabled = isFeatureFlagEnabled();
  const alreadyLoaded = isAlreadyLoaded();
  const shouldHideOnRoute = shouldHideChatOnRoute(route);

  // If not on MYACV page, hide the chat
  if (!isMyACV) {
    if (alreadyLoaded) {
      hideSalesforceChat();
    }
    return;
  }

  // If route meta says to hide chat (like My Inventory), hide it
  if (shouldHideOnRoute) {
    if (alreadyLoaded) {
      hideSalesforceChat();
    }
    return;
  }

  // On MYACV page - check feature flag
  if (!isFlagEnabled) {
    if (alreadyLoaded) {
      hideSalesforceChat();
    }
    return;
  }

  // On MYACV page with feature flag enabled
  if (alreadyLoaded) {
    window.acvEnhancedChatPrechatData = getPrechatData();
    window.dispatchEvent(new Event('acv-prechat-refresh'));
    showSalesforceChat();
    return;
  }
  // Only load if NOT already loaded and all conditions met
  if (shouldLoadEnhancedChat(route)) {
    loadSalesforceEnhancedChat();
  }
}

/**
 * Loads or shows Enhanced Chat regardless of the current route.
 * Used for explicit user actions (e.g. "Report a Problem" -> Other)
 * that should open chat no matter what page they were triggered from.
 */
export function forceLoadSalesforceEnhancedChat() {
  if (!isEnabledInEnvironment()) return;
  if (!isFeatureFlagEnabled()) return;

  if (isAlreadyLoaded()) {
    window.acvEnhancedChatPrechatData = getPrechatData();
    window.dispatchEvent(new Event('acv-prechat-refresh'));
    showSalesforceChat({ openConversation: true });
    return;
  }

  // defer until it is ready
  window.addEventListener('onEmbeddedMessagingReady', () => showSalesforceChat({ openConversation: true }), { once: true });
  loadSalesforceEnhancedChat();
}
